import { Link } from "react-router-dom";
import { useWorkflow } from "@/hooks/useWorkflow";
import Disclaimer from "@/components/Disclaimer";
import InstallAppButton from "@/components/InstallAppButton";
import DesktopShortcutButton from "@/components/DesktopShortcutButton";
import Stepper from "@/components/Stepper";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { Loading } from "@/components/workflow/ui";
import WorkflowStepRouter from "@/components/workflow/WorkflowStepRouter";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Stethoscope, LogOut, History } from "lucide-react";

const STEPS = ["Symptoms", "History Q&A", "Tentative", "Examination", "Differentials", "Investigations", "Final Dx"];
const MODELS = [
  { id: "claude-sonnet-5-5", label: "Claude Sonnet 5.5" },
  { id: "gpt-6-luna", label: "GPT-6 Luna" },
];

function WorkflowHeader({ user, logout, model, setModel }) {
  return (
    <header className="border-b border-zinc-200 px-5 sm:px-8 py-3.5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-2.5">
        <div className="h-8 w-8 rounded-md bg-[#002FA7] flex items-center justify-center">
          <Stethoscope className="h-4 w-4 text-white" />
        </div>
        <span className="font-serif text-lg tracking-tight text-zinc-900 hidden sm:inline">Medical CDSS</span>
      </div>
      <div className="flex items-center gap-3">
        <InstallAppButton />
        <DesktopShortcutButton />
        <Select value={model} onValueChange={setModel}>
          <SelectTrigger data-testid="model-select" className="h-9 w-[170px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODELS.map((m) => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {user ? (
          <>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-zinc-900 leading-tight">{user?.name}</p>
              <p className="text-xs text-zinc-500 capitalize">{user?.role}</p>
            </div>
            <Link to="/history" data-testid="history-link">
              <Button variant="outline" size="sm" className="border-zinc-300">
                <History className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">History</span>
              </Button>
            </Link>
            <ChangePasswordDialog />
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn" className="border-zinc-300">
              <LogOut className="h-4 w-4" />
            </Button>
          </>
        ) : (
          <Link to="/auth" data-testid="signin-link">
            <Button variant="outline" size="sm" className="border-zinc-300">Sign in to save</Button>
          </Link>
        )}
      </div>
    </header>
  );
}

export default function Workflow() {
  const wf = useWorkflow();
  const { user, logout, model, setModel, loading } = wf;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Disclaimer />
      <WorkflowHeader user={user} logout={logout} model={model} setModel={setModel} />

      <div className="border-b border-zinc-200 px-5 sm:px-8 py-4 bg-zinc-50">
        <Stepper steps={STEPS} current={wf.step} />
      </div>

      <main className="flex-1 px-5 sm:px-8 lg:px-12 py-8 lg:py-10 max-w-5xl w-full">
        {loading ? <Loading text={loading} /> : <WorkflowStepRouter wf={wf} user={user} />}
      </main>
    </div>
  );
}
